import { getGa4ItemsFromMagentoProductFragment, getGa4ItemsFromMagentoItemFragment } from '../utils'
import { gtmTrackGA4Event } from '../../track-event'
import { CartData, CartItem, CartObject } from '../../../core/data/quote/cart/types'
import { getPriceValues } from '../../../core/resources/helpers/index'
import { ProductData } from '../../../types/index'
import { DEFAULT_CURRENCY_CODE } from '../../../core/resources/constants/index'

export const Ga4Events = {
  /**
   * GTM GA4 purchase trigger
   */
  purchase: ({
    orderId,
    cart
  }: {
    orderId: string
    cart: CartData | undefined
  }) => {
    if (process.env.REACT_APP_ENABLE_GTM_GA4_EVENTS !== 'true') {
      return
    }
    const prices = cart?.prices

    const items = getGa4ItemsFromMagentoItemFragment({
      cartItems: cart?.items ?? []
    })

    const tax = prices?.appliedTaxes?.reduce((acc: number, element) => {
      return acc + (element?.amount?.value ?? 0)
    }, 0)

    const shipping =
      cart?.shippingAddress?.selectedShippingMethod?.amount?.value ?? 0

    gtmTrackGA4Event({
      name: 'purchase',
      params: {
        tax,
        shipping,
        items,
        currency: cart?.prices?.grandTotal?.currency ?? '',
        coupon: cart?.appliedCoupon,
        value: prices?.grandTotal?.value ?? 0,
        transaction_id: orderId
      }
    })
  },

  /**
   * GTM GA4 addToCart trigger
   */
  addToCart: ({ cartItems }: { cartItems: CartItem[] }) => {
    if (process.env.REACT_APP_ENABLE_GTM_GA4_EVENTS !== 'true') {
      return
    }

    const items = getGa4ItemsFromMagentoItemFragment({ cartItems })

    const value = cartItems.reduce((prev, current) => {
      const currentValue = current?.prices?.row_total.value ?? 0
      return prev + currentValue
    }, 0)

    const currency =
      cartItems?.[0]?.prices?.row_total?.currency ?? DEFAULT_CURRENCY_CODE

    gtmTrackGA4Event({
      name: 'add_to_cart',
      params: {
        currency,
        value,
        items
      }
    })
  },

  /**
   * GTM GA4 removeFromCart trigger
   */
  removeFromCart: ({ cartItems }: { cartItems: CartItem[] }) => {
    if (process.env.REACT_APP_ENABLE_GTM_GA4_EVENTS !== 'true') {
      return
    }
    const currency =
      cartItems?.[0]?.prices?.row_total?.currency ?? DEFAULT_CURRENCY_CODE

    const items = getGa4ItemsFromMagentoItemFragment({ cartItems })

    const value = cartItems.reduce((prev, current) => {
      const currentValue = current?.prices?.row_total.value ?? 0
      return prev + currentValue
    }, 0)

    gtmTrackGA4Event({
      name: 'remove_from_cart',
      params: {
        currency,
        value,
        items
      }
    })
  },

  /**
   * GTM GA4 viewItems trigger
   */

  viewItems: ({
    products,
    itemListName
  }: {
    products: ProductData[]
    itemListName: 'Search Page' | 'Category Page' | 'Upsell Products' | 'Other' | 'Related Products'
  }) => {
    if (process.env.REACT_APP_ENABLE_GTM_GA4_EVENTS !== 'true') {
      return
    }
    const items = getGa4ItemsFromMagentoProductFragment({ products })
    gtmTrackGA4Event({
      name: 'view_item_list',
      params: {
        item_list_id: itemListName?.toLowerCase()?.replace(' ', '-'),
        item_list_name: itemListName,
        items
      }
    })
  },

  /**
   * GTM GA4 viewItem trigger
   */

  viewItem: ({ product }: { product: ProductData }) => {
    if (process.env.REACT_APP_ENABLE_GTM_GA4_EVENTS !== 'true') {
      return
    }
    const items = getGa4ItemsFromMagentoProductFragment({ products: [product] })

    const [amount, currency] = getPriceValues(product.price)
    const price = amount ?? 0

    gtmTrackGA4Event({
      name: 'view_item',
      params: {
        currency,
        value: price,
        items
      }
    })
  },

  /**
   * GTM GA4 search trigger
   */

  search: ({ searchTerm }: { searchTerm: string }) => {
    if (process.env.REACT_APP_ENABLE_GTM_GA4_EVENTS !== 'true') {
      return
    }
    gtmTrackGA4Event({
      name: 'search',
      params: {
        search_term: searchTerm
      }
    })
  },

  /**
   * GTM GA4 Begin Checkout trigger
   */

  beginCheckout: ({ cart }: { cart?: CartObject }) => {
    if (process.env.REACT_APP_ENABLE_GTM_GA4_EVENTS !== 'true') {
      return
    }
    const items = getGa4ItemsFromMagentoItemFragment({
      cartItems: cart?.data?.items ?? []
    })
    const coupon = cart?.data?.appliedCoupon
    const value = cart?.data?.prices?.grandTotal?.value ?? 0
    const currency =
      cart?.data?.prices?.grandTotal?.currency ?? DEFAULT_CURRENCY_CODE

    gtmTrackGA4Event({
      name: 'begin_checkout',
      params: {
        currency,
        value,
        coupon,
        items
      }
    })
  },

  /**
   * GTM GA4 View Cart trigger
   */

  viewCart: ({ cart }: { cart?: CartObject }) => {
    if (process.env.REACT_APP_ENABLE_GTM_GA4_EVENTS !== 'true' || !cart) {
      return
    }
    const items = getGa4ItemsFromMagentoItemFragment({
      cartItems: cart?.data?.items ?? []
    })
    const value = cart?.data?.prices?.grandTotal?.value ?? 0
    const currency =
      cart?.data?.prices?.grandTotal?.currency ?? DEFAULT_CURRENCY_CODE

    gtmTrackGA4Event({
      name: 'view_cart',
      params: {
        currency,
        value,
        items
      }
    })
  },

  /**
   * GTM GA4 Add to WishList trigger
   */

  addToWishList: ({ product }: { product: ProductData }) => {
    if (process.env.REACT_APP_ENABLE_GTM_GA4_EVENTS !== 'true') {
      return
    }
    const items = getGa4ItemsFromMagentoProductFragment({ products: [product] })

    const [amount, currency] = getPriceValues(product?.price ?? undefined)
    const price = amount ?? 0

    gtmTrackGA4Event({
      name: 'add_to_wishlist',
      params: {
        currency,
        value: price,
        items
      }
    })
  },

  /**
   * GTM GA4 Login trigger
   */

  login: ({ method }: { method: string }) => {
    if (process.env.REACT_APP_ENABLE_GTM_GA4_EVENTS !== 'true') {
      return
    }
    gtmTrackGA4Event({
      name: 'login',
      params: {
        method
      }
    })
  },

  /**
   * GTM GA4 Add Payment Info trigger
   */

  addPaymentInfo: ({
    cart,
    paymentMethodCode
  }: {
    cart: CartObject
    paymentMethodCode: string
  }) => {
    if (process.env.REACT_APP_ENABLE_GTM_GA4_EVENTS !== 'true') {
      return
    }
    const items = getGa4ItemsFromMagentoItemFragment({
      cartItems: cart?.data?.items ?? []
    })
    const value = cart?.data?.prices?.grandTotal?.value ?? 0
    const currency =
      cart?.data?.prices?.grandTotal?.currency ?? DEFAULT_CURRENCY_CODE

    gtmTrackGA4Event({
      name: 'add_payment_info',
      params: {
        currency,
        items,
        value,
        payment_type: paymentMethodCode,
        coupon: cart?.data?.appliedCoupon ?? ''
      }
    })
  },

  /**
   * GTM GA4 Add Shipping Info trigger
   */

  addShippingInfo: ({
    cart,
    shippingTier
  }: {
    cart: CartObject
    shippingTier: string
  }) => {
    if (process.env.REACT_APP_ENABLE_GTM_GA4_EVENTS !== 'true') {
      return
    }
    const items = getGa4ItemsFromMagentoItemFragment({
      cartItems: cart?.data?.items ?? []
    })
    const value = cart?.data?.prices?.grandTotal?.value ?? 0
    const currency =
      cart?.data?.prices?.grandTotal?.currency ?? DEFAULT_CURRENCY_CODE
    gtmTrackGA4Event({
      name: 'add_shipping_info',
      params: {
        currency,
        items,
        value,
        shipping_tier: shippingTier,
        coupon: cart?.data?.appliedCoupon ?? ''
      }
    })
  },

  /**
   * GTM GA4 Sign Up trigger
   */

  signUp: ({ method }: { method: string }) => {
    if (process.env.REACT_APP_ENABLE_GTM_GA4_EVENTS !== 'true') {
      return
    }
    gtmTrackGA4Event({
      name: 'sign_up',
      params: {
        method
      }
    })
  }
}
